import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren
} from "react";
import { ApiError, apiRequest, clearNativeSessionToken, setNativeSessionToken } from "../lib/api";
import {
  canCustomerSelfManageOrder,
  customerInitialState,
  formatCustomerOrderDate,
  getOrderItemsCount,
  type CustomerAddress,
  type CustomerOrderItemSnapshot,
  type CustomerOrderRecord,
  type CustomerPaymentMethod,
  type CustomerPreferences,
  type CustomerProfile,
  type CustomerResolvedOrder,
  type CustomerState,
  type CustomerFulfillment
} from "../data/customerData";

interface SessionUser {
  id: string;
  role: "customer" | "staff" | "admin";
  name: string;
  email: string;
  phone: string;
}

interface CustomerDashboardResponse {
  user: SessionUser;
  profile: CustomerProfile;
  addresses: CustomerAddress[];
  paymentMethods: CustomerPaymentMethod[];
  preferences: CustomerPreferences;
  orders: CustomerOrderRecord[];
}

interface SessionPayload {
  token: string;
  expiresAt: string;
}

interface CustomerAuthResponse {
  user: SessionUser;
  session?: SessionPayload;
}

interface PlaceCustomerOrderInput {
  source: "site" | "app";
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  fulfillment: CustomerFulfillment;
  addressTitle: string;
  address: string;
  addressDetail: string;
  note: string;
  paymentMethodId: string;
  paymentLabel: string;
  guests: number;
  needsCutlery: boolean;
  subtotal: number;
  delivery: number;
  total: number;
  bonusAccrued: number;
  timeLabel: string;
  items: CustomerOrderItemSnapshot[];
}

interface PlaceCustomerOrderPayment {
  provider: "robokassa";
  checkoutUrl: string;
}

interface PlaceCustomerOrderResponse {
  order: CustomerOrderRecord;
  dashboard: CustomerDashboardResponse;
  payment: PlaceCustomerOrderPayment | null;
}

interface PlaceCustomerOrderResult {
  order: CustomerResolvedOrder;
  payment: PlaceCustomerOrderPayment | null;
}

interface CustomerContextValue {
  isLoading: boolean;
  isAuthenticated: boolean;
  authUser: SessionUser | null;
  profile: CustomerProfile;
  addresses: CustomerAddress[];
  paymentMethods: CustomerPaymentMethod[];
  preferences: CustomerPreferences;
  orders: CustomerResolvedOrder[];
  activeOrder: CustomerResolvedOrder | null;
  orderHistory: CustomerResolvedOrder[];
  editingOrder: CustomerResolvedOrder | null;
  stats: {
    totalOrders: number;
    activeOrders: number;
    favoriteAddress: string;
    lastOrderDate: string;
  };
  login: (input: { login: string; password: string }) => Promise<void>;
  register: (input: { name: string; email: string; phone: string; password: string }) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  saveProfile: (profile: Partial<CustomerProfile>) => Promise<void>;
  savePreferences: (preferences: CustomerPreferences) => Promise<void>;
  upsertAddress: (address: CustomerAddress) => Promise<void>;
  setPrimaryAddress: (addressId: string) => Promise<void>;
  setPrimaryPaymentMethod: (paymentMethodId: string) => Promise<void>;
  placeOrder: (input: PlaceCustomerOrderInput) => Promise<PlaceCustomerOrderResult>;
  updateOrder: (orderId: string, input: PlaceCustomerOrderInput) => Promise<PlaceCustomerOrderResult>;
  cancelOrder: (orderId: string) => Promise<void>;
  startEditingOrder: (orderId: string) => void;
  clearEditingOrder: () => void;
}

const CustomerContext = createContext<CustomerContextValue | null>(null);
const CUSTOMER_DASHBOARD_POLL_INTERVAL_MS = 15_000;

const guestState: CustomerState = {
  profile: {
    ...customerInitialState.profile,
    name: "Гость",
    firstName: "Гость",
    phone: "",
    email: "",
    tier: "Личный кабинет",
    bonusBalance: 0,
    favoriteNote: "",
    avatar: "Г",
    memberSince: "после первого входа"
  },
  addresses: [],
  paymentMethods: [
    {
      id: "guest-card",
      label: "Онлайн оплата",
      detail: "будет доступно после входа",
      primary: true
    }
  ],
  preferences: {
    removeOnionByDefault: false,
    saucesOnSide: false,
    callInsteadOfIntercom: false,
    halalSeparatePack: false,
    noCutleryByDefault: false
  },
  orders: []
};

function buildDeliverySteps(stageIndex: number) {
  const labels = [
    { id: "accepted", label: "Принят" },
    { id: "kitchen", label: "Кухня" },
    { id: "packing", label: "Сборка" },
    { id: "courier", label: "Курьер" },
    { id: "completed", label: "У двери" }
  ];

  return labels.map((step, index) => ({
    ...step,
    complete: index <= stageIndex,
    active: index === stageIndex && stageIndex < labels.length - 1
  }));
}

function buildPickupSteps(stageIndex: number) {
  const labels = [
    { id: "accepted", label: "Принят" },
    { id: "kitchen", label: "Кухня" },
    { id: "ready", label: "Готов" },
    { id: "completed", label: "Выдан" }
  ];

  return labels.map((step, index) => ({
    ...step,
    complete: index <= stageIndex,
    active: index === stageIndex && stageIndex < labels.length - 1
  }));
}

function resolveLiveOrder(order: CustomerOrderRecord): CustomerResolvedOrder {
  if (order.status === "cancelled") {
    return {
      ...order,
      stage: "completed",
      status: "Заказ отменен",
      shortStatus: "Отменен",
      eta: "Отмена подтверждена",
      lead: "Архив",
      isActive: false,
      steps: order.fulfillment === "pickup" ? buildPickupSteps(0) : buildDeliverySteps(0)
    };
  }

  if (order.paymentStatus === "pending") {
    return {
      ...order,
      stage: "accepted",
      status: "Ожидаем оплату",
      shortStatus: "Оплата",
      eta: "Переход к оплате",
      lead: "Robokassa",
      isActive: true,
      steps: order.fulfillment === "pickup" ? buildPickupSteps(0) : buildDeliverySteps(0)
    };
  }

  if (order.paymentStatus === "failed") {
    return {
      ...order,
      stage: "completed",
      status: "Оплата не завершена",
      shortStatus: "Не оплачено",
      eta: "Можно повторить",
      lead: "Оплата",
      isActive: false,
      steps: order.fulfillment === "pickup" ? buildPickupSteps(0) : buildDeliverySteps(0)
    };
  }

  const status = order.status ?? "accepted";

  if (order.fulfillment === "pickup") {
    const statusToStageIndex: Record<string, number> = {
      new: 0,
      accepted: 0,
      cooking: 1,
      packing: 2,
      courier: 2,
      completed: 3
    };
    const stageIndex = statusToStageIndex[status] ?? 0;
    const stages = [
      {
        stage: "accepted",
        status: "Принят и пошел в работу",
        shortStatus: "Принят",
        eta: "18 минут",
        lead: "Самовывоз"
      },
      {
        stage: "kitchen",
        status: "На кухне и в сборке",
        shortStatus: "Кухня",
        eta: "10 минут",
        lead: "Готовим"
      },
      {
        stage: "ready",
        status: "Готов к выдаче",
        shortStatus: "Готов",
        eta: "На стойке",
        lead: "Самовывоз"
      },
      {
        stage: "completed",
        status: "Выдан гостю",
        shortStatus: "Выдан",
        eta: "Завершен",
        lead: "Архив"
      }
    ] as const;
    const meta = stages[stageIndex];

    return {
      ...order,
      stage: meta.stage,
      status: meta.status,
      shortStatus: meta.shortStatus,
      eta: meta.eta,
      lead: meta.lead,
      isActive: meta.stage !== "completed",
      steps: buildPickupSteps(stageIndex)
    };
  }

  const statusToStageIndex: Record<string, number> = {
    new: 0,
    accepted: 0,
    cooking: 1,
    packing: 2,
    courier: 3,
    completed: 4
  };
  const stageIndex = statusToStageIndex[status] ?? 0;
  const stages = [
    {
      stage: "accepted",
      status: "Принят и подтвержден менеджером",
      shortStatus: "Принят",
      eta: "35 минут",
      lead: "Подтвержден"
    },
    {
      stage: "kitchen",
      status: "На кухне и в жаре",
      shortStatus: "Кухня",
      eta: "24 минуты",
      lead: "Готовим"
    },
    {
      stage: "packing",
      status: "Собираем и упаковываем",
      shortStatus: "Сборка",
      eta: "12 минут",
      lead: "Упаковка"
    },
    {
      stage: "courier",
      status: "Курьер уже в пути",
      shortStatus: "Курьер",
      eta: "6 минут",
      lead: "Доставка"
    },
    {
      stage: "completed",
      status: "Доставлен",
      shortStatus: "Доставлен",
      eta: "Завершен",
      lead: "Архив"
    }
  ] as const;
  const meta = stages[stageIndex];

  return {
    ...order,
    stage: meta.stage,
    status: meta.status,
    shortStatus: meta.shortStatus,
    eta: meta.eta,
    lead: meta.lead,
    isActive: meta.stage !== "completed",
    steps: buildDeliverySteps(stageIndex)
  };
}

function sortOrders(orders: CustomerOrderRecord[]) {
  return [...orders].sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime());
}

export function CustomerProvider({ children }: PropsWithChildren) {
  const [isLoading, setIsLoading] = useState(true);
  const [authUser, setAuthUser] = useState<SessionUser | null>(null);
  const [state, setState] = useState<CustomerState>(guestState);
  const [editingOrderId, setEditingOrderId] = useState<string | null>(null);

  const applyDashboard = (dashboard: CustomerDashboardResponse) => {
    setAuthUser(dashboard.user);
    setState({
      profile: dashboard.profile,
      addresses: dashboard.addresses,
      paymentMethods: dashboard.paymentMethods,
      preferences: dashboard.preferences,
      orders: sortOrders(dashboard.orders)
    });
  };

  const resetGuest = () => {
    setAuthUser(null);
    setState(guestState);
    setEditingOrderId(null);
  };

  const persistNativeSession = (session?: SessionPayload) => {
    if (session?.token) {
      setNativeSessionToken("customer", session.token);
      return;
    }

    clearNativeSessionToken("customer");
  };

  const refreshDashboard = async (options?: { silent?: boolean }) => {
    if (!options?.silent) {
      setIsLoading(true);
    }

    try {
      const dashboard = await apiRequest<CustomerDashboardResponse>("/api/customer/dashboard");
      applyDashboard(dashboard);
    } catch (error) {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        clearNativeSessionToken("customer");
        resetGuest();
      } else {
        console.error(error);
      }
    } finally {
      if (!options?.silent) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    void refreshDashboard();
  }, []);

  useEffect(() => {
    if (!authUser) {
      return;
    }

    let isCancelled = false;

    const pollDashboard = async () => {
      try {
        const dashboard = await apiRequest<CustomerDashboardResponse>("/api/customer/dashboard");

        if (isCancelled) {
          return;
        }

        applyDashboard(dashboard);
      } catch (error) {
        if (isCancelled) {
          return;
        }

        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
          clearNativeSessionToken("customer");
          resetGuest();
          return;
        }

        console.error("Customer dashboard polling failed", error);
      }
    };

    const intervalId = window.setInterval(() => {
      void pollDashboard();
    }, CUSTOMER_DASHBOARD_POLL_INTERVAL_MS);

    return () => {
      isCancelled = true;
      window.clearInterval(intervalId);
    };
  }, [authUser]);

  const orders = useMemo(() => sortOrders(state.orders).map(resolveLiveOrder), [state.orders]);
  const activeOrder = useMemo(() => orders.find((order) => order.isActive) ?? null, [orders]);
  const orderHistory = useMemo(() => orders.filter((order) => !order.isActive), [orders]);
  const editingOrder = useMemo(
    () => orders.find((order) => order.id === editingOrderId && canCustomerSelfManageOrder(order)) ?? null,
    [editingOrderId, orders]
  );
  const primaryAddress = useMemo(
    () => state.addresses.find((item) => item.primary) ?? state.addresses[0],
    [state.addresses]
  );

  useEffect(() => {
    if (!editingOrderId) {
      return;
    }

    const currentOrder = state.orders.find((order) => order.id === editingOrderId);

    if (!currentOrder || !canCustomerSelfManageOrder(currentOrder)) {
      setEditingOrderId(null);
    }
  }, [editingOrderId, state.orders]);

  const value = useMemo<CustomerContextValue>(
    () => ({
      isLoading,
      isAuthenticated: Boolean(authUser),
      authUser,
      profile: state.profile,
      addresses: state.addresses,
      paymentMethods: state.paymentMethods,
      preferences: state.preferences,
      orders,
      activeOrder,
      orderHistory,
      editingOrder,
      stats: {
        totalOrders: orders.length,
        activeOrders: orders.filter((order) => order.isActive).length,
        favoriteAddress: primaryAddress?.title ?? "Адрес не выбран",
        lastOrderDate: orders[0] ? formatCustomerOrderDate(orders[0].createdAt) : "Пока без заказов"
      },
      login: async (input) => {
        const response = await apiRequest<CustomerAuthResponse>("/api/auth/login", {
          method: "POST",
          body: JSON.stringify(input)
        });
        persistNativeSession(response.session);
        await refreshDashboard();
      },
      register: async (input) => {
        const response = await apiRequest<CustomerAuthResponse>("/api/auth/register", {
          method: "POST",
          body: JSON.stringify(input)
        });
        persistNativeSession(response.session);
        await refreshDashboard();
      },
      logout: async () => {
        try {
          await apiRequest("/api/auth/logout", {
            method: "POST"
          });
        } finally {
          clearNativeSessionToken("customer");
          resetGuest();
        }
      },
      refresh: async () => {
        await refreshDashboard();
      },
      saveProfile: async (profile) => {
        const dashboard = await apiRequest<CustomerDashboardResponse>("/api/customer/profile", {
          method: "PUT",
          body: JSON.stringify({
            name: profile.name ?? state.profile.name,
            phone: profile.phone ?? state.profile.phone,
            email: profile.email ?? state.profile.email
          })
        });
        applyDashboard(dashboard);
      },
      savePreferences: async (preferences) => {
        const dashboard = await apiRequest<CustomerDashboardResponse>("/api/customer/preferences", {
          method: "PUT",
          body: JSON.stringify(preferences)
        });
        applyDashboard(dashboard);
      },
      upsertAddress: async (address) => {
        const dashboard = await apiRequest<CustomerDashboardResponse>("/api/customer/addresses", {
          method: "POST",
          body: JSON.stringify(address)
        });
        applyDashboard(dashboard);
      },
      setPrimaryAddress: async (addressId) => {
        const dashboard = await apiRequest<CustomerDashboardResponse>(`/api/customer/addresses/${addressId}/primary`, {
          method: "PATCH"
        });
        applyDashboard(dashboard);
      },
      setPrimaryPaymentMethod: async (paymentMethodId) => {
        const dashboard = await apiRequest<CustomerDashboardResponse>(
          `/api/customer/payment-methods/${paymentMethodId}/primary`,
          {
            method: "PATCH"
          }
        );
        applyDashboard(dashboard);
      },
      placeOrder: async (input) => {
        const response = await apiRequest<PlaceCustomerOrderResponse>("/api/orders", {
          method: "POST",
          body: JSON.stringify({
            ...input,
            items: input.items.map((item) => ({
              dishId: item.dishId,
              qty: item.qty,
              removedIngredientIds: item.removedIngredientIds,
              addOnIds: item.addOnIds
            }))
          })
        });

        applyDashboard(response.dashboard);
        return {
          order: resolveLiveOrder(response.order),
          payment: response.payment
        };
      },
      updateOrder: async (orderId, input) => {
        const response = await apiRequest<PlaceCustomerOrderResponse>(`/api/orders/${orderId}`, {
          method: "PUT",
          body: JSON.stringify({
            ...input,
            items: input.items.map((item) => ({
              dishId: item.dishId,
              qty: item.qty,
              removedIngredientIds: item.removedIngredientIds,
              addOnIds: item.addOnIds
            }))
          })
        });

        applyDashboard(response.dashboard);
        return {
          order: resolveLiveOrder(response.order),
          payment: response.payment
        };
      },
      cancelOrder: async (orderId) => {
        const dashboard = await apiRequest<CustomerDashboardResponse>(`/api/orders/${orderId}/cancel`, {
          method: "POST"
        });
        applyDashboard(dashboard);

        if (editingOrderId === orderId) {
          setEditingOrderId(null);
        }
      },
      startEditingOrder: (orderId) => {
        const order = state.orders.find((item) => item.id === orderId);

        if (!order || !canCustomerSelfManageOrder(order)) {
          throw new Error("Редактировать можно только неоплаченный заказ.");
        }

        setEditingOrderId(orderId);
      },
      clearEditingOrder: () => {
        setEditingOrderId(null);
      }
    }),
    [activeOrder, authUser, editingOrder, editingOrderId, isLoading, orderHistory, orders, primaryAddress, state]
  );

  return <CustomerContext.Provider value={value}>{children}</CustomerContext.Provider>;
}

export function useCustomer() {
  const context = useContext(CustomerContext);

  if (!context) {
    throw new Error("useCustomer must be used within CustomerProvider");
  }

  return context;
}

export function useCustomerOrderSummary(order: CustomerResolvedOrder | null) {
  return useMemo(() => {
    if (!order) {
      return null;
    }

    return {
      itemsCount: getOrderItemsCount(order),
      dateLabel: formatCustomerOrderDate(order.createdAt)
    };
  }, [order]);
}
